import { SIMULATION_INSURANCE_TYPE } from '@/constants/simulation.const';
import {
	CalculateSimulationRequest,
	GetSimulationDetailResponse,
} from '@/libs/fleet-api/services/simulation';
import { FileData } from '@/libs/fleet-api/services/types';
import { UploadOutlined } from '@ant-design/icons';
import { Col, Divider, Form, Row, Upload, UploadFile } from 'antd';
import { FormInstance } from 'antd/es/form';
import React from 'react';
import SimInsuranceBankerClause from './SimInsurance.BankerClause';
import SimInsuranceMTFClause from './SimInsurance.MTFClause';
import { NamePath } from 'antd/es/form/interface';
import { useModals } from '@/components/modals/ModalProvider';

/**
 * Props for SimInsurance
 * TODO: Replace 'unknown' with the actual types for simulationDetailData and uploadMutation
 */
interface SimInsuranceProps {
	form: FormInstance;
	fieldName: (name: NamePath<CalculateSimulationRequest>) => typeof name;
	simulationDetailData: GetSimulationDetailResponse; // TODO: type this properly
	uploadMutation: unknown; // TODO: type this properly
	facilityId: number;
	viewOnly?: boolean;
	tenorInYear?: number;
}

type InsuranceUploadFieldName =
	| 'insurancePolicyDocument'
	| 'insurancePaymentDocument';

/**
 * SimInsurance renders the insurance-related form fields for the simulation page.
 * Handles all insurance type branches (MTF_CLAUSE, BANKER_CLAUSE, COMBINATION).
 *
 * @param {SimInsuranceProps} props - Insurance form props
 * @returns {Element}
 */
const SimInsurance: React.FC<SimInsuranceProps> = ({
	form,
	fieldName,
	simulationDetailData,
	uploadMutation,
	facilityId,
	viewOnly = false,
	tenorInYear,
}) => {
	const { openPreviewModal } = useModals();

	// Helper for rendering upload fields
	const renderUploadField = (
		name: InsuranceUploadFieldName,
		label: string,
		initialValue: string | undefined,
		isViewOnly: boolean = viewOnly,
	) => (
		<>
			<Form.Item
				name={fieldName([name, 'id'])}
				noStyle
				initialValue={initialValue}
				label={label}
			/>
			<Form.Item
				name={fieldName([name])}
				label={label}
				rules={[{ required: true, message: `${label} wajib diisi` }]}
				getValueProps={(value) => {
					const file = value as FileData;
					return {
						fileList: file?.id
							? ([
									{
										uid: file.id,
										name: file.name,
										thumbUrl: file.url,
									},
								] as UploadFile[])
							: [],
					};
				}}
				normalize={({ file }: { file: UploadFile }) => ({
					id: file.uid,
					format: file.type,
					name: file.name,
					url: file.thumbUrl,
				})}
			>
				<Upload.Dragger
					listType="picture"
					accept=".pdf, .doc, .docx"
					maxCount={1}
					disabled={
						isViewOnly ||
						(
							uploadMutation as unknown as {
								isPending: boolean;
								mutateAsync: (args: {
									file: File;
									fileid: string;
									filepath: string;
								}) => Promise<string>;
							}
						).isPending
					} // TODO: type uploadMutation properly
					beforeUpload={() => false}
					onRemove={() => {
						if (!isViewOnly) {
							form.setFieldValue(fieldName([name]), null);
							return false;
						}
						return false;
					}}
					onPreview={() => {
						const fileData = form.getFieldValue(fieldName(name));
						if (fileData && fileData.url) {
							openPreviewModal({
								id: fileData.id,
								name: fileData.name,
								url: fileData.url,
								format: fileData.format || 'pdf',
							});
						}
					}}
					onChange={async ({ file }) => {
						const fileid = Date.now().toString();
						const filepath = `customer/${simulationDetailData.customerId}/${facilityId}/simulasi`;
						try {
							const url = await (
								uploadMutation as unknown as {
									isPending: boolean;
									mutateAsync: (args: {
										file: File;
										fileid: string;
										filepath: string;
									}) => Promise<string>;
								}
							).mutateAsync({
								// TODO: type uploadMutation properly
								file: file as unknown as File,
								fileid,
								filepath,
							});
							form.setFieldValue(fieldName(name), {
								format: 'jpeg',
								id: fileid,
								name: file.name,
								path: filepath,
								url,
							});
						} catch {
							form.setFieldValue(fieldName(name), null);
						}
					}}
				>
					<UploadOutlined style={{ fontSize: 12, display: 'block' }} />
				</Upload.Dragger>
			</Form.Item>
		</>
	);

	const renderInsuranceSection = (
		insuraceType: SIMULATION_INSURANCE_TYPE,
		insuranceOrder?: number,
	): JSX.Element => {
		switch (insuraceType) {
			case SIMULATION_INSURANCE_TYPE.MTF_CLAUSE:
				return (
					<SimInsuranceMTFClause
						fieldName={fieldName}
						simulationDetailData={simulationDetailData}
						insuranceOrder={insuranceOrder}
						viewOnly={viewOnly}
						tenorInYear={tenorInYear}
					/>
				);
			case SIMULATION_INSURANCE_TYPE.BANKER_CLAUSE:
				return (
					<SimInsuranceBankerClause
						renderUploadField={
							renderUploadField as unknown as (
								name: string,
								label: string,
								initialValue: string | undefined,
								isViewOnly?: boolean,
							) => JSX.Element
						}
						simulationDetailData={simulationDetailData}
						insuranceOrder={insuranceOrder}
						fieldName={fieldName}
						viewOnly={viewOnly}
					/>
				);
			case SIMULATION_INSURANCE_TYPE.REKANAN:
			case SIMULATION_INSURANCE_TYPE.COMBINATION:
				// Prevent infinite recursion
				if (
					simulationDetailData.firstInsuranceType ===
						SIMULATION_INSURANCE_TYPE.COMBINATION ||
					simulationDetailData.secondInsuranceType ===
						SIMULATION_INSURANCE_TYPE.COMBINATION
				) {
					return <></>;
				}
				return (
					<>
						<Row gutter={40}>
							<Col span={12}>
								<Form.Item label="Tipe Asuransi">
									{/* {simulationDetailData?.insuranceType} */}
									Kombinasi
								</Form.Item>
							</Col>
						</Row>
						<Divider />

						{renderInsuranceSection(
							simulationDetailData.firstInsuranceType as SIMULATION_INSURANCE_TYPE,
							1,
						)}
						<Divider />
						{renderInsuranceSection(
							simulationDetailData.secondInsuranceType as SIMULATION_INSURANCE_TYPE,
							2,
						)}
					</>
				);
			default:
				return <></>;
		}
	};

	// Note: Initial values are handled by the parent component (SimulationPage)
	// using mapSimulationDetailToFormValues which correctly extracts IDs from OptionType objects

	return renderInsuranceSection(
		simulationDetailData.insuranceType as SIMULATION_INSURANCE_TYPE,
	);
};

export default SimInsurance;
